/*
 * File: interceptor.tsx
 * Project: a-a
 * File Created: Tuesday, 15th October 2024 7:56:12 pm
 * Author: Jackson Thounaojam (jackson.thounaojam@mutualmobile.com)
 * -----
 * Last Modified: Monday, 19th January 2026 10:21:08 pm
 * Modified By: Jackson Thounaojam (jackson.thounaojam@mutualmobile.com)
 * -----
 * Copyright 2020 - 2024 Mutual Mobile, Mutual Mobile
 */

import RoutePaths from '../routes/RoutePaths';
import { AuthService } from '../service/auth.service';
// import axios from './axios';
import axios from '../core/axios';
import { AsanaService } from '../service/asana.service';
import { parse } from 'path';

axios.interceptors.request.use(
  async (config: any) => {
    const token = sessionStorage.getItem('user-token') || '';
    const asanaToken = localStorage.getItem('asana-token') || '';
    let accessToken: any = {};
    if (token) {
      accessToken = JSON.parse(token);
    }
    if (
      ![
        'validate_user?',
        'login',
        'create_user',
        '/refresh_token',
        '/ms_login_tokens?code=',
        '/get_huddle_info',
        'logout',
        'https://huddle-users-dev.s3.us-east-1.amazonaws.com/',
        'https://huddle-users-prod.s3.us-east-1.amazonaws.com/',
      ].includes(config?.url)
    ) {
      config.headers.Authorization = `Bearer ${accessToken?.IdToken}`;

      // Add access header for ADMIN API calls
      if (config?.url.includes('/admin/')) {
        config.headers['access'] = accessToken?.AccessToken;
      }

      if (
        config?.url.split('/').includes('asana') &&
        config?.url !== '/asana/auth/validate-token' &&
        config?.url !== '/asana/auth/logout'
      ) {
        const asanaCredentials = localStorage.getItem('asana-cred') || '';
        const parsedCreds = asanaCredentials ? JSON.parse(asanaCredentials) : {};
        config.headers['asana-token'] = asanaToken;
      }
    }

    return config;
  },
  (error) => {
    Promise.reject(error);
  },
);

axios.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    console.log('Error refreshing token:', error);
    if (error?.response?.data === 'Refresh Token has been revoked') {
      sessionStorage.clear();
      window.location.replace(RoutePaths.Login);
    }
    const originalRequest = error.config;
    const token = sessionStorage.getItem('user-token') || '';
    const asanaCred = localStorage.getItem('asana-cred') || '';
    let asanaRefreshToken: any = {};

    if (asanaCred) {
      asanaRefreshToken = JSON.parse(asanaCred).refresh_token;
    }

    let accessToken: any = {};
    if (
      originalRequest._retry ||
      originalRequest._refreshAttempted ||
      (error.status === 500 && originalRequest?.url !== 'logout')
    ) {
      return Promise.reject(error);
    }
    if (token) {
      accessToken = JSON.parse(token);
      // console.log(accessToken);
    }

    const expiryTime = sessionStorage.getItem('loginExpiry') || '';
    const refreshTime = sessionStorage.getItem('refreshToken') || '';
    const asanaExpiry = localStorage.getItem('asana-expiry') || '';
    const now = new Date();
    // const fiveHourLater = new Date(now.getTime() + 60 * 60 * 1000 * 5);

    if (new Date(expiryTime) < now) {
      // console.log(new Date(expiryTime) < now);
      sessionStorage.clear();
      window.location.replace(RoutePaths.Login);
      return;
    }
    if (new Date(refreshTime) < now || originalRequest?.url === 'logout') {
      // console.log({ check: new Date(refreshTime) < now });
      originalRequest._refreshAttempted = true;
      try {
        const res = await AuthService.refreshToken(accessToken?.RefreshToken, accessToken?.IdToken);
        // console.log(res);
        sessionStorage.setItem(
          'user-token',
          JSON.stringify({
            ...res.AuthenticationResult,
            RefreshToken: accessToken?.RefreshToken,
          }),
        );
        const now = new Date();

        const oneHourLater = new Date(now.getTime() + 60 * 60 * 1000);
        // Save in sessionStorage as a string

        sessionStorage.setItem('refreshToken', oneHourLater.toString());
        if (originalRequest.url.includes('/asana') && asanaExpiry && new Date(asanaExpiry) < now) {
          const res = await AsanaService.asanaRefreshToken({
            refresh_token: asanaRefreshToken, // Use the refresh token from sessionStorage
          });

          // Update the sessionStorage with the new Asana credentials
          localStorage.setItem('asana-token', res?.access_token || '');
          localStorage.setItem(
            'asana-cred',
            JSON.stringify({
              ...res,
              refresh_token: asanaRefreshToken,
            }),
          );

          // Calculate new expiry time (3600 seconds from now)
          const newExpiryTime = new Date();
          newExpiryTime.setSeconds(newExpiryTime.getSeconds() + 3600);
          localStorage.setItem('asana-expiry', newExpiryTime.toISOString());
        }
        return axios(originalRequest); // Retry original request
      } catch (err) {
        return Promise.reject(error);
      }
    }

    if (error.response?.status === 401) {
      originalRequest._retry = true;
      // Check if the endpoint contains "/asana"
      if (originalRequest.url.includes('/asana')) {
        try {
          const res = await AsanaService.asanaRefreshToken({
            refresh_token: asanaRefreshToken, // Use the refresh token from sessionStorage
          });

          // Update the sessionStorage with the new Asana credentials
          localStorage.setItem('asana-token', res?.access_token || '');
          localStorage.setItem(
            'asana-cred',
            JSON.stringify({
              ...res,
              refresh_token: asanaRefreshToken,
            }),
          );

          // Update the Authorization header with the new access token
          originalRequest.headers['asana-token'] = `Bearer ${res?.access_token}`;
          return axios(originalRequest); // Retry original request
        } catch (err) {
          console.log('Error refreshing Asana token:', err);
          // sessionStorage.clear();
          return Promise.reject(error);
        }
      } else {
        try {
          const res = await AuthService.refreshToken(
            accessToken?.RefreshToken,
            accessToken?.IdToken,
          );
          // console.log(res);
          sessionStorage.setItem(
            'user-token',
            JSON.stringify({
              ...res.AuthenticationResult,
              RefreshToken: accessToken?.RefreshToken,
            }),
          );
          originalRequest.headers['Authorization'] = `Bearer ${res.AuthenticationResult?.IdToken}`;
          return axios(originalRequest); // Retry original request
        } catch (err) {
          console.log('Error refreshing token:', err);
          // sessionStorage.clear();
          // window.location.replace(window.location.origin);
          return Promise.reject(error);
        }
      }
    } else if (error.response?.status === 500 || !accessToken?.AccessToken) {
      console.log('500', error);
      return Promise.reject(error);
    } else if (!accessToken?.RefreshToken) {
      return Promise.reject(error);
    } else if (
      error.response?.status === 502 ||
      error.response?.status === 400 ||
      error.response?.status === 404
    ) {
      console.log('Bad request', error.response?.data); // Log the error data
      return Promise.reject(error);
    } else if (error.code === 'ERR_NETWORK') {
      if (error.config.url === 'login') {
        // window.location.replace(window.location.origin);
        return Promise.reject('Network Error');
      } else if (error.config.url === 'logout') {
        // remove this later
        sessionStorage.clear();
        window.location.replace(window.location.origin);
      }
    }
  },
);
